import { PageInfoBar } from "@components/common/page-info-bar";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Stack } from "@mui/material";
import { EditableTeamName } from "@pages/members/teams/team-details/editable-team-name";
import { getTeamDetailsInfoBarAction } from "@pages/members/teams/team-details/team-details-info-bar-action";
import { useAppParams } from "@router/router-helper";
import { useAppSelector } from "@store/store-helper";
import {
  selectedTeamAsMemberSelector,
  selectedTeamWithRoleSelector,
} from "@store/teams/teams-selector";
import { WorkspaceRoleDropdown } from "@components/role/workspace-role-dropdown";
import { TeamIcon } from "@flat-ui";

interface Props {
  /** Flag whether the content should be shown as skeletons because it is still loading */
  isLoading?: boolean;
}

/**
 * Info bar for the team overview tabs
 */
export function TeamDetailsInfoBar({ isLoading = false }: Props): JSX.Element {
  const selectedTeam = useAppSelector(selectedTeamWithRoleSelector);
  const selectedTeamAsMember = useAppSelector(selectedTeamAsMemberSelector);
  const { canEditTeams } = useHasUserValidRoleCompanyLevel();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { teamTabs } = useAppParams();

  if (!selectedTeam || !selectedTeamAsMember || isLoading) {
    // Early exit with a loading skeleton if the team is not yet loaded
    return <PageInfoBar isLoading={true} />;
  }

  return (
    <PageInfoBar
      items={[
        {
          columnSize: 2,
          content: (
            <Stack flexDirection={"row"} gap={"12px"} alignItems={"center"}>
              <SphereAvatar
                icon={<TeamIcon sx={{ width: "26px", height: "26px" }} />}
                size="medium"
                backgroundColor={
                  teamDisplayProperties[selectedTeam.category.kind].color
                }
                shouldHideWhiteRim={true}
              />
              <Stack width="100%">
                <EditableTeamName
                  team={selectedTeam}
                  isEditable={canEditTeams}
                />
              </Stack>
            </Stack>
          ),
        },
        {
          columnSize: 2,
          content: <WorkspaceRoleDropdown member={selectedTeamAsMember} />,
        },
      ]}
      action={{
        content: getTeamDetailsInfoBarAction({ selectedTeam, teamTabs }),
        isHidden:
          isScreenSmOrSmaller ||
          !getTeamDetailsInfoBarAction({ selectedTeam, teamTabs }),
      }}
    />
  );
}
